import { z } from 'zod'
import {
  COLOURS,
  MAX_BOARD_NAME,
  MAX_DESCRIPTION,
  MAX_PROJECT_DESCRIPTION,
  MAX_PROJECT_NAME,
  MAX_TAG_NAME,
  MAX_TITLE,
  QUADRANTS,
  STATUSES,
  TAG_COLOURS,
} from './constants'

export const idSchema = z.string().uuid()
export const boardNameSchema = z.string().trim().min(1).max(MAX_BOARD_NAME)
export const titleSchema = z.string().trim().min(1).max(MAX_TITLE)
export const descriptionSchema = z.string().max(MAX_DESCRIPTION).default('')
export const quadrantSchema = z.enum(QUADRANTS)
export const colourSchema = z.enum(COLOURS)
export const statusSchema = z.enum(STATUSES)
export const tagColourSchema = z.enum(TAG_COLOURS)
export const tagNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(MAX_TAG_NAME)
  .regex(
    /^[\p{L}\p{N}][\p{L}\p{N}\s._-]*$/u,
    'Use letters, numbers, spaces, dots, dashes, or underscores',
  )
export const projectNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(MAX_PROJECT_NAME)
  .regex(/[\p{L}\p{N}]/u, 'Use at least one letter or number')
export const projectDescriptionSchema = z.string().max(MAX_PROJECT_DESCRIPTION).default('')

export const createBoardSchema = z.object({ name: boardNameSchema })
export const updateBoardSchema = createBoardSchema.partial()

export const createTagSchema = z.object({
  name: tagNameSchema,
  colour: tagColourSchema.default('slate'),
})
export const updateTagSchema = createTagSchema
  .partial()
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be changed',
  })

export const createProjectSchema = z.object({
  name: projectNameSchema,
  description: projectDescriptionSchema.optional(),
})
export const updateProjectSchema = z
  .object({
    name: projectNameSchema.optional(),
    description: projectDescriptionSchema.optional(),
    version: z.number().int().positive(),
  })
  .refine((value) => Object.keys(value).some((key) => key !== 'version'), {
    message: 'At least one field must be changed',
  })

export const createItemSchema = z.object({
  id: idSchema,
  title: titleSchema,
  description: descriptionSchema.optional(),
  colour: colourSchema.default('amber'),
  quadrant: quadrantSchema,
  position: z.number().int().safe().optional(),
  tagIds: z.array(idSchema).max(20).optional(),
  projectId: idSchema.nullable().optional(),
})

export const updateItemSchema = z
  .object({
    title: titleSchema.optional(),
    description: descriptionSchema.optional(),
    colour: colourSchema.optional(),
    quadrant: quadrantSchema.optional(),
    position: z.number().int().safe().optional(),
    tagIds: z.array(idSchema).max(20).optional(),
    projectId: idSchema.nullable().optional(),
    version: z.number().int().positive(),
  })
  .refine((value) => Object.keys(value).some((key) => key !== 'version'), {
    message: 'At least one field must be changed',
  })

export const versionSchema = z.object({ version: z.number().int().positive() })

export const reorderSchema = z.object({
  itemId: idSchema,
  quadrant: quadrantSchema,
  beforeId: idSchema.nullable().optional(),
  afterId: idSchema.nullable().optional(),
  version: z.number().int().positive(),
})

export const listItemsQuerySchema = z.object({
  status: statusSchema.optional(),
  quadrant: quadrantSchema.optional(),
  colour: colourSchema.optional(),
  tag: idSchema.optional(),
  project: idSchema.optional(),
  q: z.string().trim().max(200).optional(),
  sort: z.enum(['position', 'updated', 'created', 'title', 'completed']).default('position'),
  direction: z.enum(['asc', 'desc']).default('asc'),
  limit: z.coerce.number().int().min(1).max(1000).default(500),
})

export const signUpModeSchema = z.enum(['disabled', 'invite_only', 'open'])

const importItemSchema = z.object({
  id: idSchema.optional(),
  title: titleSchema,
  description: descriptionSchema.optional(),
  colour: colourSchema,
  quadrant: quadrantSchema,
  position: z.number().int().safe().optional(),
  status: statusSchema,
  createdAt: z.number().int().optional(),
  updatedAt: z.number().int().optional(),
  completedAt: z.number().int().nullable().optional(),
  deletedAt: z.number().int().nullable().optional(),
  tags: z.array(createTagSchema).max(20).optional(),
})

const importBoardV1Schema = z.object({
  id: idSchema.optional(),
  name: boardNameSchema,
  items: z.array(importItemSchema),
})

const importBoardV2Schema = z.object({
  id: idSchema.optional(),
  name: boardNameSchema,
  projects: z.array(
    z.object({
      id: idSchema,
      name: projectNameSchema,
      description: projectDescriptionSchema.optional(),
      closedAt: z.number().int().nullable().optional(),
    }),
  ),
  items: z.array(importItemSchema.extend({ projectId: idSchema.nullable().optional() })),
})

const importV1Schema = z.object({
  schemaVersion: z.literal(1),
  boards: z.array(importBoardV1Schema).min(1).max(100),
})

const importV2Schema = z.object({
  schemaVersion: z.literal(2),
  boards: z.array(importBoardV2Schema).min(1).max(100),
})

export const importSchema = z.discriminatedUnion('schemaVersion', [importV1Schema, importV2Schema])

export type CreateItemInput = z.infer<typeof createItemSchema>
export type UpdateItemInput = z.infer<typeof updateItemSchema>
export type CreateTagInput = z.infer<typeof createTagSchema>
export type UpdateTagInput = z.infer<typeof updateTagSchema>
export type CreateProjectInput = z.infer<typeof createProjectSchema>
export type UpdateProjectInput = z.infer<typeof updateProjectSchema>
export type ReorderInput = z.infer<typeof reorderSchema>
export type ListItemsQuery = z.infer<typeof listItemsQuerySchema>
export type ImportPayload = z.infer<typeof importSchema>
